import { ReviewPagePaging } from "./reviewPagePaging";
/** Timestamp string representing the epoch origin, used as a fallback when no date is present. */
const defaultTime = new Date(0, 0, 0).getTime().toString();
/**
 * Reset all filter inputs within the given filter panel to their default (unfiltered) state.
 * @param filterPanel The filter panel element containing the filter inputs to be reset.
 */
function resetFilters(filterPanel) {
    const verifiedCheckbox = filterPanel.querySelector('#verified-only');
    const repliedCheckbox = filterPanel.querySelector('#replied-only');
    const timespanInput = filterPanel.querySelector('#timespan');
    const ratingCheckboxesContainer = filterPanel.querySelector('#rating');
    let ratingCheckboxes = null;
    if (ratingCheckboxesContainer != null) {
        ratingCheckboxes = ratingCheckboxesContainer.querySelectorAll('.dropdown-option input[type="checkbox"]');
    }
    if (verifiedCheckbox != null) {
        verifiedCheckbox.checked = false;
    }
    if (repliedCheckbox != null) {
        repliedCheckbox.checked = false;
    }
    if (timespanInput != null) {
        timespanInput.value = 'all';
    }
    if (ratingCheckboxes != null) {
        ratingCheckboxes.forEach((checkbox) => {
            checkbox.checked = false;
        });
    }
}
/**
 * Read the current filter panel selections and apply them to every review in the
 * container, marking filtered-out reviews with {@link filteredReviewClass}.
 * Resets paging to the first page after filtering.
 * @param reviewContainer The review container element whose reviews will be filtered.
 * @param filterPanel The filter panel element from which the active filter values are read.
 */
function filterReviews(reviewContainer, filterPanel) {
    let reviews = reviewContainer.querySelector('.reviews-container');
    if (reviews == null)
        return;
    let reviewsArray = reviews.querySelectorAll('.review');
    const verifiedCheckbox = filterPanel.querySelector('#verified-only');
    const repliedCheckbox = filterPanel.querySelector('#replied-only');
    const timespanInput = filterPanel.querySelector('#timespan');
    const ratingCheckboxesContainer = filterPanel.querySelector('#rating');
    let ratingCheckboxes = null;
    if (ratingCheckboxesContainer != null) {
        ratingCheckboxes = ratingCheckboxesContainer.querySelectorAll('.dropdown-option input[type="checkbox"]');
    }
    let allowedRatings = [];
    if (ratingCheckboxes != null) {
        ratingCheckboxes.forEach((checkbox) => {
            if (checkbox.checked) {
                allowedRatings = allowedRatings.concat(checkbox.value);
            }
        });
    }
    reviewsArray.forEach((review) => {
        let filter = false;
        if (!filter && verifiedCheckbox != null) {
            filter = filterVerified(verifiedCheckbox.checked, review);
        }
        if (!filter && repliedCheckbox != null) {
            filter = filterReplied(repliedCheckbox.checked, review);
        }
        if (!filter && timespanInput != null) {
            filter = filterTimeSpan(timespanInput.value, review);
        }
        if (!filter && ratingCheckboxes != null) {
            filter = filterRating(allowedRatings, review);
        }
        if (filter) {
            review.classList.add('filtered-review');
        }
        else {
            review.classList.remove('filtered-review');
        }
    });
    // Shows the first page of results after filtering and resets the load more button.
    const loadMoreBtn = reviews.querySelector('.load-more-button');
    ReviewPagePaging.showHideReviews(reviewsArray, 10, 0, loadMoreBtn);
    if (loadMoreBtn != null)
        loadMoreBtn.dataset['page'] = '0';
}
/**
 * Evaluates the provided review's verified status against the given only verified boolean.
 * @param onlyVerified The boolean for if reviews should be evaluated for their verified status.
 * @param review The Review to be evaluated for it's verified status.
 * @returns TRUE if the review should be filtered out and FALSE if the review should be shown.
 */
function filterVerified(onlyVerified, review) {
    return onlyVerified && review.dataset.verified === 'false';
}
/**
 * Evaluates the provided review's replied status against the given only replied boolean.
 * @param onlyReplied The boolean for if reviews should be evaluated for their replied status.
 * @param review The Review to be evaluated for a reply.
 * @returns TRUE if the review should be filtered out and FALSE if the review should be shown.
 */
function filterReplied(onlyReplied, review) {
    return (onlyReplied && review.dataset.replyDate === '');
}
/**
 * Evaluate the provided review's review date against the selected dropdown timespan.
 * @param timespan The String Value of the TimeSpan field.
 * @param review The Review to be evaluated against the given timespan.
 * @returns TRUE if the review should be filtered out and FALSE if the review should be shown.
 */
function filterTimeSpan(timespan, review) {
    var _a;
    let reviewDateString = (_a = review.dataset.reviewDate) !== null && _a !== void 0 ? _a : '';
    //If no date is present in the dataset values default to the start of time.
    if (reviewDateString === '')
        reviewDateString = defaultTime;
    // Get the Review Date from the Review
    const reviewDate = parseInt(reviewDateString);
    // If the Time Span is set then evaluate the reviews date against the given timespan
    if (timespan !== 'all') {
        const today = new Date();
        let limitDate = today.getTime();
        // Update the LimitDate variable to match dropdown requirement
        switch (timespan) {
            case 'past-week':
                limitDate = today.setDate(today.getDate() - 7);
                break;
            case 'past-month':
                limitDate = today.setMonth(today.getMonth() - 1);
                break;
            case 'past-6-months':
                limitDate = today.setMonth(today.getMonth() - 6);
                break;
            case 'past-year':
                limitDate = today.setFullYear(today.getFullYear() - 1);
                break;
            default:
                break;
        }
        // If the card is more recent than the limit date return false (don't filter)
        if (reviewDate > limitDate) {
            return false;
        }
        else {
            return true;
        }
    }
    else { // All dates are accepted
        return false;
    }
}
/**
 * Evaluate the provided review's rating against the allowed Rating Array.
 * @param allowedRatings The Array of allowed ratings to be shown in the final array list.
 * @param review The Review to be evaluated for filtering.
 * @returns TRUE if the review should be filtered out and FALSE if the review should be shown.
 */
function filterRating(allowedRatings, review) {
    var _a;
    const rating = (_a = review.dataset.rating) !== null && _a !== void 0 ? _a : '5';
    if (allowedRatings.length > 0) {
        if (allowedRatings.includes(rating)) {
            return false;
        }
        else {
            return true;
        }
    }
    else {
        return false;
    }
}
export class ReviewPageFilters {
    /**
 * Attach click listeners to the filter and clear-filter buttons inside the
 * given review container so that filters are applied or reset accordingly.
 * @param reviewContainer The review container element that scopes the filter panel.
 */
    static setupFilters(reviewContainer) {
        const filterPanel = reviewContainer.querySelector('.filter-panel .filters');
        if (filterPanel == null)
            return;
        const resetBtn = filterPanel.querySelector('#clear-filter-btn');
        const filterBtn = filterPanel.querySelector('#filter-btn');
        if (resetBtn != null) {
            resetBtn.addEventListener('click', () => {
                resetFilters(filterPanel);
                filterReviews(reviewContainer, filterPanel);
            });
        }
        if (filterBtn != null) {
            filterBtn.addEventListener('click', () => {
                filterReviews(reviewContainer, filterPanel);
            });
        }
    }
}
